import { ColDef, ICellRendererParams, themeAlpine } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import get from "lodash.get";
import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";

import {
  ExperienceActionType,
  ExperienceListDto,
  TabEnumFe,
} from "@intelligems/ig-types";
import { checkOfferLimit } from "@intelligems/ig-utils/offer";
import { Grid, GridProps, styled, useTheme } from "@mui/material";
import { useGetSummaryMetrics } from "@src/api/analytics/get-summary-metrics";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { usePauseExperience } from "@src/api/experience-actions/pause-experience";
import { useStartExperience } from "@src/api/experience-actions/start-experience";
import { useGetExperiencesList } from "@src/api/experiences-list/get-experiences-list";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useGetAndSetAllSchedules } from "@src/api/schedules/get-all-schedules";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { ActiveScheduleDialog } from "@src/components/Dialogs/ActiveScheduleDialog";
import { ExperienceInfoDrawer } from "@src/components/Drawer/ExperienceInfoDrawer";
import { HistoryDrawer } from "@src/components/Drawer/HistoryDrawer";
import { checkBooleanScope } from "@src/components/LimitAccess/check-scopes";
import { useCheckSubscriptionStatus } from "@src/components/LimitAccess/check-subscriptions-hooks";
import {
  ComponentsCellRenderer,
  MoreMenuCellRenderer,
} from "@src/features/offers/components/OffersTableCellRenderers";
import {
  getPersonalizationDuration,
  isUsedInExperiment,
} from "@src/features/personalizations/utils";
import { SchedulePersonalizationDialog } from "@src/features/scheduleExperience/SchedulePersonalizationDialog";
import { ArchivePersonalizationDialog } from "@src/features/sharedExperience/components/alertsAndWarnings/ArchivePersonalizationDialog";
import { OfferLimitReachedDialog } from "@src/features/sharedExperience/components/alertsAndWarnings/OfferLimitReachedDialog";
import { RelatedExperienceActiveDialog } from "@src/features/sharedExperience/components/alertsAndWarnings/RelatedExperienceActiveDialog";
import { UnarchivePersonalizationDialog } from "@src/features/sharedExperience/components/alertsAndWarnings/UnarchivePersonalizationDialog";
import {
  SharedActionColumn,
  SharedStatusColumn,
  SharedTargetingColumn,
  SharedTargetingColumnHeader,
  SharedTitleColumn,
} from "@src/features/sharedExperience/components/sharedTableRenderers";
import { onPersonalizationActionClick } from "@src/features/sharedExperience/tableUtils";
import { PERSONALIZATION_ROUTES } from "@src/routes/routes";
import { isDrawerOpen } from "@src/store/slices/drawers";
import { getDialogPropDetails, isDialogOpen } from "@src/store/slices/global";
import { theme } from "@src/theme";

const StyledGrid = styled(({ ...props }: GridProps) => <Grid {...props} />)(
  ({ theme }) => ({
    [`& .ag-root-wrapper`]: {
      border: "none",
      fontFamily: `${theme.typography.fontFamily}`,
      fontSize: "14px",
      fontWeight: 400,
    },
    [`& .ag-header-cell-text`]: {
      fontSize: "14px",
      color: theme.palette.text.secondary,
    },
    [`& .ag-group-value`]: {
      width: "100%",
    },
    [`& .ag-header`]: {
      borderBottom: `solid 1px ${theme.igBorders.core.default} !important`,
    },
    [`& .ag-header-cell-label`]: {
      justifyContent: "flex-start",
      fontWeight: 400,
      marginBottom: "-5px",
    },
    [`& .ag-row`]: {
      borderBottom: `solid 1px ${theme.igBorders.core.default} !important`,
    },
    [`& .ag-row-hovered`]: {
      textDecoration: "underline",
      cursor: "pointer",
    },
  })
);

const myTheme = themeAlpine.withParams({
  fontFamily: theme.typography.fontFamily,
  fontSize: "14px",
});

export interface BaseExperienceTableProps {
  renderLocation: "siteContent" | "offers" | "experiences";
  rowData: ExperienceListDto[];
  isLoading: boolean;
  defaultColDef?: ColDef;
}

export const BaseExperienceTable = ({
  renderLocation,
  rowData,
  defaultColDef,
  isLoading,
}: BaseExperienceTableProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isInfoDrawerOpen = useSelector(isDrawerOpen("experienceInfo"));
  const isHistoryDrawerOpen = useSelector(isDrawerOpen("history"));

  const isSubscriptionActive = useCheckSubscriptionStatus(true);
  const isAdmin = useIsAdmin();
  const org = useGetOrg();

  const pauseExperience = usePauseExperience();
  const startExperience = useStartExperience();
  const experiments = useGetExperiencesList({
    category: "experiment",
  });
  const summaryMetrics = useGetSummaryMetrics();
  const { schedules, isLoading: isSchedulesLoading } =
    useGetAndSetAllSchedules();

  const isScheduleDialogOpen = useSelector(
    isDialogOpen("schedulePersonalization")
  );
  const personalizations = useGetExperiencesList({
    category: "personalization",
  });
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const archiveDialogProps = useSelector(
    getDialogPropDetails("archivePersonalization")
  );
  const unarchiveDialogProps = useSelector(
    getDialogPropDetails("unarchivePersonalization")
  );

  const [columnExpanded, setColumnExpanded] = useImmer(false);

  const onChipClick = (id: string) => {
    void navigate(
      `${PERSONALIZATION_ROUTES.Personalization}/${id}?action=edit&tab=${TabEnumFe.TARGETING}`
    );
  };

  const isUnderOfferLimit = useMemo(() => {
    if (
      personalizations.isSuccess &&
      renderLocation === "offers" &&
      org.data?.org
    ) {
      return checkOfferLimit(
        org.data.org,
        personalizations.data.experiencesList || [],
        experiments.data?.experiencesList || []
      );
    }
    return true;
  }, [
    personalizations.isSuccess,
    renderLocation,
    org.data?.org,
    experiments.data?.experiencesList,
  ]);

  const onExpandClick = () => {
    setColumnExpanded((draft) => !draft);
  };

  const hasPriceModAccess = checkBooleanScope(
    "start_price_experiment",
    org.data.org.configurations
  );
  const hasOfferModAccess = checkBooleanScope(
    "start_discount_experiment",
    org.data.org.configurations
  );

  const onActionClick = (personalization: ExperienceListDto) => {
    const pathParams = {
      experienceId: personalization.id,
      name: personalization.name,
      action:
        personalization.status === "started"
          ? ("pause" as ExperienceActionType)
          : ("start" as ExperienceActionType),
      pausedAtTs: null,
    };

    const experienceMutation =
      personalization.status === "started"
        ? pauseExperience.mutate
        : startExperience.mutate;

    // TODO - CP get mp event hooked up

    experienceMutation({
      pathParams,
      queryParams: undefined,
      body: {
        variationId: undefined,
        updatePricesToVariation: false,
      },
    });
  };

  const getIsUsedInTest = useCallback(
    (experienceId: string) => {
      if (experiments.isSuccess && experienceId) {
        return isUsedInExperiment(
          experiments.data.experiencesList,
          experienceId
        );
      }
      return { name: undefined, id: undefined };
    },
    [experiments.isSuccess, experiments.data?.experiencesList]
  );

  const defaultColDefValue = useMemo<ColDef>(() => {
    return {
      resizable: false,
      cellStyle: (params) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        minHeight: "52px",
        color: params.data?.archivedAtTs
          ? theme.palette.text.tertiary
          : theme.palette.text.primary,
      }),
      menuTabs: [],
      suppressMovable: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      suppressHeaderMenuButton: true,
      sortable: false,
      ...defaultColDef,
    };
  }, [defaultColDef]);

  const baseColumnDefs: ColDef[] = useMemo(() => {
    const base: ColDef[] = [
      {
        headerName: "Title",
        field: "name",
        flex: 1.5,
        cellRenderer: SharedTitleColumn,
        cellRendererParams: {
          orgConfigurations: org.data.org.configurations,
          storeCurrencyConfig: storeCurrencyConfig!.value,
        },
      },
      {
        headerName: "Date",
        field: "createdAtTs",
        flex: 1,
        cellRenderer: (params: ICellRendererParams) => {
          return getPersonalizationDuration(
            params.data,
            storeCurrencyConfig!.value.locale,
            schedules
          );
        },
      },
      {
        headerName: "Status",
        field: "status",
        flex: 0.8,
        cellRenderer: SharedStatusColumn,
        cellRendererParams: {
          schedules,
        },
      },
      {
        headerName: "Targeting",
        field: "targeting",
        flex: 1,
        autoHeight: true,
        wrapText: true,
        cellRenderer: SharedTargetingColumn,
        headerComponent: SharedTargetingColumnHeader,
        headerComponentParams: {
          columnExpanded,
          onExpandClick,
        },
        cellRendererParams: {
          onGeneralChipClick: onChipClick,
          columnExpanded,
        },
      },
      {
        headerName: "Visitors",
        field: "visitors",
        flex: renderLocation === "offers" ? 0.5 : 0.4,
        cellRenderer: (params: ICellRendererParams) => {
          if (summaryMetrics.isSuccess) {
            const experienceTotals = get(
              summaryMetrics,
              `data[${params.data.id}].visitors`,
              null
            ) as string | null;
            return experienceTotals;
          }
          return "";
        },
      },
      {
        headerName: "",
        field: "action",
        flex: 0.5,
        cellRendererParams: {
          hasPriceModAccess,
          hasOfferModAccess,
          inactiveSubscriptionGate: !isAdmin && !isSubscriptionActive,
          schedules,
          isSchedulesLoading,
          experimentsList: experiments.data?.experiencesList || [],
          getIsUsedInTest,
          isUnderOfferLimit,
          isAdmin,
          onActionClick,
        },
        cellRenderer: SharedActionColumn,
      },
      {
        headerName: "",
        field: "",
        flex: 0.3,
        cellRendererParams: {
          unarchiveDialogProps,
          archiveDialogProps,
          experiments: experiments?.data?.experiencesList || [],
          org: org?.data?.org || {},
          schedules,
          isSchedulesLoading,
          getIsUsedInTest,
        },
        cellRenderer: MoreMenuCellRenderer,
      },
    ];
    if (renderLocation === "offers") {
      base.splice(5, 0, {
        headerName: "Components",
        field: "widgets",
        flex: 0.7,
        valueGetter: (params) => {
          return params.data.widgets || [];
        },
        cellRenderer: ComponentsCellRenderer,
      });
    }
    return base;
  }, [
    experiments.data?.experiencesList,
    unarchiveDialogProps,
    archiveDialogProps,
    org.data?.org,
    columnExpanded,
    summaryMetrics.isSuccess,
    isAdmin,
    isSubscriptionActive,
    schedules,
    isSchedulesLoading,
    getIsUsedInTest,
    isUnderOfferLimit,
    renderLocation,
  ]);

  return (
    <>
      <StyledGrid
        sx={{
          width: "100%",
          maxHeight: "500px",
          height: "500px",
          minHeight: "500px",
        }}
      >
        <AgGridReact
          theme={myTheme}
          domLayout={"autoHeight"}
          rowHeight={52}
          defaultColDef={defaultColDefValue}
          columnDefs={baseColumnDefs}
          rowData={rowData}
          loading={isLoading}
          suppressDragLeaveHidesColumns={true}
          suppressExcelExport={true}
          suppressCsvExport={true}
          suppressContextMenu={true}
          suppressRowHoverHighlight={true}
          suppressCellFocus={true}
          getRowId={(params) => params.data.id}
        />
      </StyledGrid>
      {isScheduleDialogOpen && <SchedulePersonalizationDialog />}
      {isHistoryDrawerOpen && <HistoryDrawer />}
      <ArchivePersonalizationDialog
        experienceId={archiveDialogProps?.experienceId}
        shouldNavigate={false}
      />
      <ActiveScheduleDialog
        handleEnableToggle={onActionClick}
        organization={org.data.org}
      />
      <RelatedExperienceActiveDialog handleEnableToggle={onActionClick} />
      <UnarchivePersonalizationDialog
        experienceId={unarchiveDialogProps?.experienceId}
        shouldNavigate={false}
      />
      {isInfoDrawerOpen && <ExperienceInfoDrawer />}
      <OfferLimitReachedDialog />
    </>
  );
};
